import React, { useEffect, useState } from 'react';
import { DeviceCalibration as Pt, DeviceDetail, getDevice } from '../../api/device';
import { ApiError } from '../../api/request';
import { useCan, useDeviceCtx } from '../../store';
import CalibrationImportModal from './CalibrationImportModal';
import DeviceTopSelector from './DeviceTopSelector';
import PowerCalcWidget from './PowerCalcWidget';

// 功率校准子页. curDevice 与 Profile / YAML 子页通过 store 共享.
// 提供 % ↔ W 双向插值 (% → W 与 W → %) 与校准曲线 + 校准点表.
// (换算服务绑定入口在「设备树/工况」页顶部的当前设备栏, 存 device.convert_tool_key 固定列.)

function CalibChart({ points, stdW }: { points: Pt[]; stdW: number }) {
    if (points.length === 0) {
        return <div className="text-xs text-slate-400">无校准点</div>;
    }
    const W = 600;
    const H = 160;
    const pad = 28;
    const stdWVal = Math.max(stdW || 1, ...points.map((p) => p.realW));
    const x = (pct: number) => pad + (pct / 100) * (W - 2 * pad);
    const y = (w: number) => H - pad - (w / stdWVal) * (H - 2 * pad);
    const sorted = [...points].sort((a, b) => a.pct - b.pct);
    const path = sorted.map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(p.pct)} ${y(p.realW)}`).join(' ');
    return (
        <svg width={W} height={H} className="block max-w-full">
            {[0, 25, 50, 75, 100].map((g) => (
                <line key={g} x1={x(g)} y1={pad} x2={x(g)} y2={H - pad} stroke="#f1f5f9" />
            ))}
            <line x1={pad} y1={H - pad} x2={W - pad} y2={H - pad} stroke="#cbd5e1" />
            <line x1={pad} y1={pad} x2={pad} y2={H - pad} stroke="#cbd5e1" />
            <path d={path} fill="none" stroke="#3b6cf6" strokeWidth={2} />
            {sorted.map((p) => (
                <circle key={p.pct} cx={x(p.pct)} cy={y(p.realW)} r={2.5} fill="#3b6cf6" />
            ))}
            {[0, 25, 50, 75, 100].map((g) => (
                <text key={g} x={x(g)} y={H - 10} textAnchor="middle" fontSize="10" fill="#94a3b8">
                    {g}%
                </text>
            ))}
            <text x={W - pad} y={H - pad - 4} textAnchor="end" fontSize="10" fill="#94a3b8">
                软件功率%
            </text>
            <text x={pad + 4} y={pad + 10} fontSize="10" fill="#94a3b8">
                真实功率 W
            </text>
        </svg>
    );
}

function DeviceCalibration() {
    const ctx = useDeviceCtx();
    const canUpdate = useCan('device', 'update');
    const [detail, setDetail] = useState<DeviceDetail | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [importOpen, setImportOpen] = useState(false);
    // 导入直接入库后 +1 触发重拉 (走审批时数据未变, 不刷)
    const [refreshTick, setRefreshTick] = useState(0);

    useEffect(() => {
        if (!ctx.curDeviceKey) {
            setDetail(null);
            return;
        }
        setLoading(true);
        getDevice(ctx.curDeviceKey)
            .then((d) => {
                setDetail(d);
                setError('');
            })
            .catch((e) => {
                const msg = e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
                setError(msg);
                setDetail(null);
            })
            .finally(() => setLoading(false));
    }, [ctx.curDeviceKey, refreshTick]);

    const calibration = detail?.calibration || [];
    const stdW = detail?.capability?.stdW || 0;

    return (
        <div className="space-y-5">
            <div className="card p-4 flex items-center justify-between gap-3 flex-wrap">
                <DeviceTopSelector label="功率校准 · 当前设备" />
                {canUpdate && (
                    <button
                        type="button"
                        className="btn-primary"
                        onClick={() => setImportOpen(true)}
                        title="上传 xlsx 模板或设备 YAML (power_calibration 段), 按 device_key 整曲线覆盖目标设备的校准点"
                    >
                        导入校准点 (xlsx / yaml)
                    </button>
                )}
            </div>
            <CalibrationImportModal
                open={importOpen}
                onClose={() => setImportOpen(false)}
                onImported={() => setRefreshTick((t) => t + 1)}
            />

            {error && (
                <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50">
                    {error}
                </div>
            )}

            <div className="grid grid-cols-3 gap-4">
                <div className="card p-4 col-span-2">
                    <div className="text-sm font-semibold mb-2">校准曲线</div>
                    {loading ? (
                        <div className="text-xs text-slate-400">加载中…</div>
                    ) : (
                        <CalibChart points={calibration} stdW={stdW} />
                    )}
                </div>
                <div className="card p-4 col-span-1">
                    <div className="text-sm font-semibold mb-2">% ↔ W 双向换算</div>
                    <PowerCalcWidget
                        showTitle={false}
                        showDeviceSelect={false}
                        stacked
                        deviceKey={ctx.curDeviceKey}
                    />
                </div>
            </div>

            <div className="card overflow-x-auto">
                <div className="px-4 py-3 text-sm font-semibold border-b border-slate-100">
                    全部校准点 (共 {calibration.length} 点)
                </div>
                <table className="w-full">
                    <thead className="bg-slate-50">
                        <tr>
                            <th className="th">软件功率 %</th>
                            <th className="th">真实功率 W</th>
                        </tr>
                    </thead>
                    <tbody>
                        {calibration.length === 0 && (
                            <tr>
                                <td className="td text-center text-slate-400" colSpan={2}>
                                    暂无数据
                                </td>
                            </tr>
                        )}
                        {[...calibration]
                            .sort((a, b) => a.pct - b.pct)
                            .map((p) => (
                                <tr key={p.pct} className="hover:bg-slate-50">
                                    <td className="td font-mono">{p.pct}</td>
                                    <td className="td font-mono">{p.realW}</td>
                                </tr>
                            ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default DeviceCalibration;
